import React, {
  useState,
  useLayoutEffect,
  useCallback,
  useEffect,
} from "react";
import { StyleSheet, Text, View } from "react-native";
import { colors } from "src/assets";
import { UserCard, CallOffer, Typography, Chat } from "src/components";
import { StackNavigationProp } from "@react-navigation/stack";
import { CustomTextEditNavigationParam } from "src/screens/custom-text-edit";
import {
  useUser,
  useCognito,
  useCall,
  useChatMessages,
  useChatMessagesActions,
} from "src/hooks";
import { v4 as uuid } from "uuid";
import { RequestStatus } from "src/domains";
import { EventRegister } from "react-native-event-listeners";
import { CallChatCloseEvent } from "src/utils/event";
import { IConversationMessage, IConversationMessageInput } from "src/domains";
import { useNavigation, useRoute } from "@react-navigation/native";
import { RootStackScreenProps } from "src/navigation/types";

export type CallChatNavigationParam = {
  callId: string;
};

export const CallChatScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"CallChat">["navigation"]>();
  const route = useRoute<RootStackScreenProps<"CallChat">["route"]>();
  const [cognitoUsername] = useCognito();
  const [call] = useCall(route.params.callId);
  const [user] = useUser(
    call?.senderId === cognitoUsername ? call?.opponentId : call?.senderId
  );
  useLayoutEffect(() => {
    navigation.setOptions({ title: user?.name ? `${user.name}さん` : "" });
  }, [navigation, user]);
  useEffect(() => {
    const callChatCloseListener = EventRegister.addEventListener(
      CallChatCloseEvent,
      () => navigation.goBack()
    );
    return () => {
      EventRegister.removeEventListener(callChatCloseListener as string);
    };
  }, [navigation]);

  const [messages] = useChatMessages(route.params.callId);
  const [sendingMessages, setSendingMessages] = useState<
    Array<IConversationMessage>
  >([]);
  const { conversationMessage } = useChatMessagesActions();

  useEffect(() => {
    setSendingMessages((a) =>
      a.filter((b) => !messages.find((c) => c.id === b.id))
    );
  }, [messages]);

  const sendHandler = useCallback(
    ({ text }: { text?: string }) => {
      if (!call) {
        return;
      }
      const input: IConversationMessageInput = {
        id: uuid() as string,
        conversationId: route.params.callId,
        opponentId:
          call?.senderId === cognitoUsername
            ? call?.opponentId
            : call?.senderId,
        text: text,
      };
      setSendingMessages((a) => [
        ...a,
        {
          id: input.id,
          conversationId: input.conversationId,
          ownerId: cognitoUsername || "unknown",
          opponentId: input.opponentId,
          content: {
            text: input.text,
          },
        },
      ]);
      conversationMessage({
        input,
        type: "call-chack",
      });
    },
    [call, cognitoUsername]
  );

  return (
    <View style={styles.root}>
      <View style={styles.content}>
        {user ? (
          <UserCard
            contanerStyle={styles.user}
            source={user?.image ? { uri: user?.image } : undefined}
            // status={user?.status}
            name={user?.name}
            birthday={user?.birthday}
            gender={user?.gender}
            prefecture={user?.address?.prefecture}
            isVerified={user?.identificationStatus === "APPROVED"}
            onPress={() =>
              navigation.push("User", {
                id: user.id,
                title: "",
                hideStatus: true,
              })
            }
          />
        ) : null}
        {cognitoUsername && user ? (
          <Chat
            containerStyle={styles.chat}
            userId={cognitoUsername}
            messages={[...sendingMessages, ...messages]}
            onSend={sendHandler}
          />
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.chokuGray11,
  },
  content: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.white,
  },
  chat: {
    flex: 1,
  },
  user: {
    marginTop: 16,
    marginBottom: 8,
    marginHorizontal: 16,
  },
});
